import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
	TextField,
	Button,
	Typography,
	Box,
	Paper,
	Grid,
	Snackbar,
	Alert,
	Autocomplete,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions
} from "@mui/material";
import { getTodayUTCDateString } from "../../utils/calculations";
import { auditedAddItem } from "../../firebase/auditLog/wrappedOperations";
import { savePartnerInvestment } from "../../firebase/partnerInvestment/savePartnerInvestment";
import { getPartnerInvestmentsByDateRange } from "../../firebase/partnerInvestment/getPartnerInvestmentsByDateRange";
import { authorizedUsersService } from "../../firebase/authorization/authorizedUsersService";
import { useShopCash } from "../../hooks/useShopCash";
import { ItemInfo } from "../../models/item";
import ItemsList from "./ItemsList";
import { getTotalItemsCount } from "../../firebase/itemsAndInventory/getItems";
import { getItemById } from "../../firebase/itemsAndInventory/getItemById";
import { AppDispatch } from "../../redux/store";
import { useDispatch } from "react-redux";
import { fetchItems } from "../../redux/slices/itemsSlice";
import {
	buildPartnerInvestmentPayload,
	createInvestmentItemLinksForSelection,
	validatePartnerInvestmentDraft
} from "../../utils/partnerInvestmentFlow";
import { Inventory2 } from "@mui/icons-material";

export const AddNewItem = () => {
	const navigate = useNavigate();
	const [itemCode, setItemCode] = useState("");
	const [itemDescription, setItemDescription] = useState("");
	const [purchasePrice, setPurchasePrice] = useState("");
	const [salePrice, setSalePrice] = useState("");
	const [minProfitPercent, setMinProfitPercent] = useState("");
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState<
		"success" | "error"
	>("error");
	const [availableQuantity, setAvailableQuantity] = useState("");
	const [partnerName, setPartnerName] = useState("");
	const [partnerOptions, setPartnerOptions] = useState<string[]>([]);
	const [fundingSource, setFundingSource] = useState<"partner" | "shopCash">(
		"partner"
	);
	const [shopCashRef, setShopCashRef] = useState<string | null>(null);
	const { cashMovements, fetchShopCashData } = useShopCash();
	const [investmentAmount, setInvestmentAmount] = useState("");
	const [investmentDate, setInvestmentDate] = useState(
		new Date().toLocaleDateString("en-CA")
	);
	const [investmentDescription, setInvestmentDescription] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [refreshListKey, setRefreshListKey] = useState(0);
	const [selectedItemKeys, setSelectedItemKeys] = useState<string[]>([]);
	const [selectedItemsMap, setSelectedItemsMap] = useState<{
		[key: string]: ItemInfo;
	}>({});
	const [recordDialogOpen, setRecordDialogOpen] = useState(false);
	const dispatch = useDispatch<AppDispatch>();

	useEffect(() => {
		const generateItemCode = async () => {
			const count = await getTotalItemsCount();
			const paddedCount = (count + 1).toString().padStart(4, "0");
			setItemCode(`FB-${paddedCount}`);
		};
		generateItemCode();

		const loadPartners = async () => {
			try {
				const active = await authorizedUsersService.getActivePartners();
				const partners = active
					.map((p) => p.displayName?.trim() || p.email)
					.filter(Boolean) as string[];
				setPartnerOptions(partners);
			} catch (err) {
				setPartnerOptions([]);
			}
		};
		void loadPartners();

		// load recent shop cash entries for linking if needed
		const currentDate = new Date().toLocaleDateString("en-CA");
		const defaultStartDate = "2026-01-01";
		void fetchShopCashData(defaultStartDate, currentDate);
	}, []);

	const handleCloseSnackbar = (
		event?: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenSnackbar(false);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		// Handle form submission logic here
		console.log({
			itemCode,
			itemDescription,
			purchasePrice,
			salePrice,
			minProfitPercent
		});
		try {
			await auditedAddItem({
				itemCode,
				name: itemDescription,
				purchasePrice: parseFloat(purchasePrice),
				sellPrice: parseFloat(salePrice),
				minProfitPercent: parseFloat(minProfitPercent),
				createdAt: getTodayUTCDateString(),
				availableQuantity: parseInt(availableQuantity, 10)
			} as ItemInfo);

			// New items are added without creating an investment record here.
			// Investment records are created later from the separate item-selection flow.
			// Show success snackbar
			setSnackbarMessage("Item added successfully!");
			setSnackbarSeverity("success");
			setOpenSnackbar(true);
			// Clear form
			setItemDescription("");
			setPurchasePrice("");
			setSalePrice("");
			setMinProfitPercent("");
			setAvailableQuantity("");
			// Generate new item code
			const count = await getTotalItemsCount();
			const paddedCount = (count + 1).toString().padStart(4, "0");
			setItemCode(`FB-${paddedCount}`);
			// Refresh items in Redux store after invoice creation
			dispatch(fetchItems());
			// Trigger ItemsList to re-render
			setRefreshListKey((prev) => prev + 1);
			// clear selection after add
			setSelectedItemKeys([]);
			setSelectedItemsMap({});

			// also close record dialog if open
			setRecordDialogOpen(false);
		} catch (error) {
			// Show error snackbar
			setSnackbarMessage("Failed to add item.");
			setSnackbarSeverity("error");
			setOpenSnackbar(true);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Handlers for Record Investment modal
	const openRecordDialog = () => setRecordDialogOpen(true);
	const closeRecordDialog = () => setRecordDialogOpen(false);

	const handleRecordInvestmentSubmit = async () => {
		const validationError = validatePartnerInvestmentDraft({
			fundingSource,
			partnerName,
			amount: investmentAmount,
			investmentDate,
			description: investmentDescription,
			shopCashRef
		});
		if (validationError) {
			setSnackbarMessage(validationError);
			setSnackbarSeverity("error");
			setOpenSnackbar(true);
			return;
		}

		setIsSubmitting(true);
		try {
			const existingInvestments = await getPartnerInvestmentsByDateRange(
				"2000-01-01",
				new Date().toLocaleDateString("en-CA")
			);
			const { itemLinks, error } =
				await createInvestmentItemLinksForSelection({
					selectedItemKeys,
					selectedItemsByKey: selectedItemsMap,
					existingInvestments,
					resolveItemByKey: async (itemKey) => {
						try {
							return await getItemById(itemKey);
						} catch (err) {
							return null;
						}
					}
				});
			if (error) {
				setSnackbarMessage(error);
				setSnackbarSeverity("error");
				setOpenSnackbar(true);
				setIsSubmitting(false);
				return;
			}

			const inv = buildPartnerInvestmentPayload({
				fundingSource,
				partnerName,
				amount: investmentAmount,
				investmentDate,
				description:
					investmentDescription ||
					"Bulk investment for selected items",
				itemLinks,
				shopCashRef,
				createdAt: getTodayUTCDateString(),
				userId: "unknown",
				username: "Unknown"
			});
			await savePartnerInvestment(inv);
			setSnackbarMessage("Investment recorded");
			setSnackbarSeverity("success");
			setOpenSnackbar(true);
			setSelectedItemKeys([]);
			setSelectedItemsMap({});
			setRecordDialogOpen(false);
		} catch (err) {
			console.error(err);
			setSnackbarMessage("Failed to record investment");
			setSnackbarSeverity("error");
			setOpenSnackbar(true);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<Paper elevation={3} sx={{ p: 3, mt: 3 }}>
				<Snackbar
					open={openSnackbar}
					autoHideDuration={6000}
					onClose={handleCloseSnackbar}
				>
					<Alert
						onClose={handleCloseSnackbar}
						severity={snackbarSeverity}
						sx={{ width: "100%" }}
					>
						{snackbarMessage}
					</Alert>
				</Snackbar>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 2,
						gap: 1
					}}
				>
					<Typography variant="h5">Add New Item</Typography>
					<Button
						variant="contained"
						startIcon={<Inventory2 />}
						onClick={() => navigate("/items")}
					>
						Inventory : All Items
					</Button>
				</Box>
				<form onSubmit={handleSubmit}>
					<Grid container spacing={2}>
						<Grid size={12}>
							<TextField
								label="Item Code"
								value={itemCode}
								fullWidth
								required
								disabled
							/>

							<Dialog
								open={recordDialogOpen}
								onClose={closeRecordDialog}
								fullWidth
								maxWidth="sm"
							>
								<DialogTitle>
									Record Investment for Selected Items
								</DialogTitle>
								<DialogContent>
									<Box
										sx={{ display: "grid", gap: 1, mt: 1 }}
									>
										<TextField
											label="Funding Source"
											select
											value={fundingSource}
											onChange={(e) =>
												setFundingSource(
													e.target.value as
														| "partner"
														| "shopCash"
												)
											}
										>
											<MenuItem value={"partner"}>
												Partner
											</MenuItem>
											<MenuItem value={"shopCash"}>
												Shop Cash
											</MenuItem>
										</TextField>

										{fundingSource === "partner" ? (
											<TextField
												label="Partner"
												value={partnerName}
												onChange={(e) =>
													setPartnerName(
														e.target.value
													)
												}
												select
												SelectProps={{ native: true }}
											>
												<option value="">
													Select or type partner
												</option>
												{partnerOptions.map((p) => (
													<option key={p} value={p}>
														{p}
													</option>
												))}
											</TextField>
										) : (
											<Autocomplete
												options={cashMovements.filter(
													(c) => c.type === "EXPENSE"
												)}
												getOptionLabel={(opt) =>
													`${opt.movementDate.substring(0, 10)} - ${opt.description} - Rs. ${opt.amount}`
												}
												isOptionEqualToValue={(o, v) =>
													o.id === v.id
												}
												onChange={(_e, val) =>
													setShopCashRef(
														val ? val.id : null
													)
												}
												renderInput={(params) => (
													<TextField
														{...params}
														label="Link ShopCash Expense"
													/>
												)}
											/>
										)}

										<TextField
											label="Investment Amount"
											type="number"
											value={investmentAmount}
											onChange={(e) =>
												setInvestmentAmount(
													e.target.value
												)
											}
										/>
										<TextField
											label="Investment Date"
											type="date"
											value={investmentDate}
											onChange={(e) =>
												setInvestmentDate(
													e.target.value
												)
											}
											InputLabelProps={{ shrink: true }}
										/>
										<TextField
											label="Investment Description"
											value={investmentDescription}
											onChange={(e) =>
												setInvestmentDescription(
													e.target.value
												)
											}
											multiline
											minRows={2}
										/>
									</Box>
								</DialogContent>
								<DialogActions>
									<Button onClick={closeRecordDialog}>
										Cancel
									</Button>
									<Button
										onClick={handleRecordInvestmentSubmit}
										variant="contained"
										disabled={isSubmitting}
									>
										Record
									</Button>
								</DialogActions>
							</Dialog>
						</Grid>
						<Grid size={12}>
							<TextField
								label="Item Description"
								value={itemDescription}
								onChange={(e) =>
									setItemDescription(e.target.value)
								}
								fullWidth
								required
							/>
						</Grid>
						<Grid size={6}>
							<TextField
								label="Purchase Price"
								type="number"
								value={purchasePrice}
								onChange={(e) =>
									setPurchasePrice(e.target.value)
								}
								fullWidth
								required
							/>
						</Grid>
						<Grid size={6}>
							<TextField
								label="Sale Price"
								type="number"
								value={salePrice}
								onChange={(e) => setSalePrice(e.target.value)}
								fullWidth
								required
							/>
						</Grid>
						<Grid size={12}>
							<TextField
								label="Minimum Profit Percent"
								type="number"
								value={minProfitPercent}
								onChange={(e) =>
									setMinProfitPercent(e.target.value)
								}
								fullWidth
								required
							/>
						</Grid>
						<Grid size={12}>
							<TextField
								label="Available Quantity"
								type="number"
								value={availableQuantity}
								onChange={(e) =>
									setAvailableQuantity(e.target.value)
								}
								fullWidth
								required
							/>
						</Grid>
						<Grid size={12} />
						<Grid size={12}>
							<Box sx={{ mt: 2 }}>
								<Button
									type="submit"
									variant="contained"
									color="primary"
									disabled={isSubmitting}
								>
									Add Item
								</Button>
							</Box>
						</Grid>
					</Grid>
				</form>
			</Paper>
			<ItemsList
				lastDays={1}
				key={refreshListKey}
				selectable
				selectedKeys={selectedItemKeys}
				onSelectionChange={(keys, itemsMap) => {
					setSelectedItemKeys(keys || []);
					setSelectedItemsMap(itemsMap || {});
				}}
				onOpenRecord={openRecordDialog}
			/>
		</>
	);
};
